(async () => {
    const { Core } = await importModule('Core');
    const { $fetch, debounce, $toggleDisplay, $replaceContent, scrollLock } = await importModule('Utils');
    
    const ATTR_ONLY_STOCK = 'only-stock-products';
    const ATTR_MIN_CHARS = 'min-chars';
    const ATTR_LIMIT = 'results-limit';
    const ATTR_WITH_SUGGESTIONS = 'with-suggestions';
    const ATTR_WITH_RECENT = 'with-recent';
    const ATTR_SOURCES = 'sources';
    const RQ_SECTION = 'r_predictive-search';
    const ATTR_NO_RESULTS = 'data-empty-results';
    const DEFAULT_MIN_CHARS = 3;
    const DEFAULT_LIMIT = 5;
    // Recherche par code : section rendue sur /search (voir sections/r_search-codes.liquid)
    const RQ_CODES_SECTION = 'r_search-codes';
    const CODE_MIN_CHARS = 4;
    // CERAMED: index des SKU distributeur en 128 seaux ; MÊME formule que outils/code-index/build-index.py (bucket_of)
    const CODE_INDEX_BUCKETS = 128;
    const bucketOf = (prefix) => {
        let h = 5381;
        for (let i = 0; i < prefix.length; i++) h = (Math.imul(h, 33) + prefix.charCodeAt(i)) >>> 0;
        return h % CODE_INDEX_BUCKETS;
    };
    // CERAMED: version de l'index = champ « version » de code-index-manifest.json (relu au plus une fois par heure,
    // d'où le paramètre v : le CDN de Shopify ne distingue les versions d'un fichier QUE par ?v= et ignore tout autre
    // paramètre, donc un ?t= servirait l'ancien manifeste pendant un an). Elle est ajoutée en ?v= à chaque fichier d'index : le CDN les garde un an en cache,
    // donc sans version un navigateur ne verrait jamais l'index de la nuit. Pas de manifeste = fonction inactive.
    let codeIndexVersion = null;
    const getCodeIndexVersion = (tpl) => {
        if (!codeIndexVersion) {
            const url = tpl.replace('{p}', 'manifest') + (tpl.includes('?') ? '&' : '?') + 'v=' + Math.floor(Date.now() / 3600000);
            codeIndexVersion = fetch(url)
                .then(r => (r.ok ? r.json() : null))
                .then(m => {
                    const v = m && m.version ? String(m.version) : null;
                    if (!v) codeIndexVersion = null;
                    return v;
                })
                .catch(() => {
                    codeIndexVersion = null;
                    return null;
                });
        }
        return codeIndexVersion;
    };
    // CERAMED: règle « ressemble à un code » et recherche dans l'index des codes, PARTAGÉES par la liste déroulante
    // (méthode _fetchIndexItems, getter looksLikeCode) et par l'encadré « Résultats par code » de la page /search
    // (assets/search-code-results.js, via window.ceramedCodeIndex). Ne jamais dupliquer cette logique ni bucketOf.
    // Un code peut être saisi avec des espaces n'importe où (« R 56914 », « RNRD 2796 », « 400 368 355 »). Garde-fous pour ne pas
    // prendre une phrase pour un code : 4 morceaux au plus, chacun fait de lettres/chiffres/._/-, aucun mot de 5 lettres ou plus
    // (« tuile 12 » reste une recherche de texte), au moins un chiffre, 4 caractères ou plus une fois les espaces retirés.
    const looksLikeCode = (q) => {
        const tokens = String(q).trim().split(/\s+/);
        if (tokens.length > 4) return false;
        if (!tokens.every(t => /^[A-Za-z0-9][A-Za-z0-9._/-]*$/.test(t) && !/^[A-Za-z]{5,}$/.test(t))) return false;
        const compact = tokens.join('');
        return compact.length >= CODE_MIN_CHARS && /[0-9]/.test(compact);
    };
    const fetchCodeIndexItems = async (query, limit) => {
        const tpl = window.codeIndex && window.codeIndex.url;
        if (!tpl) return [];
        const qn = String(query).toLowerCase().replace(/[^a-z0-9]/g, '');
        if (qn.length < 3) return [];
        const shard = await loadCodeShard(tpl, qn);
        if (!shard) return [];
        const keys = Object.keys(shard)
            .filter(k => k.startsWith(qn))
            .sort((a, b) => (a === qn ? -1 : b === qn ? 1 : a.length - b.length || (a < b ? -1 : 1)));
        const picked = [];
        const seen = new Set();
        for (const key of keys) {
            const vals = [].concat(shard[key]);
            for (const val of vals) {
                const [handle, variant] = val.split('|');
                if (seen.has(handle)) continue;
                seen.add(handle);
                picked.push({ handle, variant, exact: key === qn });
                if (picked.length >= limit) break;
            }
            if (picked.length >= limit) break;
        }
        return buildCodeItems(picked);
    };
    // CERAMED: le seau d'un code (3 premiers caractères du code normalisé), null si l'index est absent
    const loadCodeShard = async (tpl, qn) => {
        const version = await getCodeIndexVersion(tpl);
        if (!version) return null;
        const sep = tpl.includes('?') ? '&' : '?';
        const res = await fetch(tpl.replace('{p}', bucketOf(qn.slice(0, 3))) + sep + 'v=' + encodeURIComponent(version));
        return res.ok ? res.json() : null;
    };
    const buildCodeItems = async (picked) => {
        const base = (dynamicURLs.root || '/');
        const items = await Promise.all(picked.map(async (p) => {
            try {
                const r = await fetch(`${base}products/${p.handle}?variant=${p.variant}&section_id=r_code-item`);
                if (!r.ok) return null;
                const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
                const el = doc.querySelector('[data-code-item]');
                const item = el && el.firstElementChild;
                if (!item) return null;
                if (p.exact) {
                    item.setAttribute('data-exact', '');
                    item.setAttribute('data-exact-url', `${base}products/${p.handle}`);
                }
                return item;
            } catch (e) { return null; }
        }));
        return items.filter(Boolean);
    };
    // CERAMED: codes PROCHES quand rien ne correspond (page /search seulement) : même seau que la saisie, donc sans requête de
    // plus. Distance d'édition de 1 (2 dès 9 caractères) ou longue partie commune au début ; une faute dans les 3 premiers
    // caractères n'est pas rattrapée (autre seau). Minimum 5 caractères pour éviter le bruit.
    const editDistance = (a, b, max) => {
        if (Math.abs(a.length - b.length) > max) return max + 1;
        let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
        for (let i = 1; i <= a.length; i++) {
            const cur = [i];
            let rowMin = i;
            for (let j = 1; j <= b.length; j++) {
                cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
                if (cur[j] < rowMin) rowMin = cur[j];
            }
            if (rowMin > max) return max + 1;
            prev = cur;
        }
        return prev[b.length];
    };
    const fetchSimilarCodeItems = async (query, limit) => {
        const tpl = window.codeIndex && window.codeIndex.url;
        if (!tpl) return [];
        const qn = String(query).toLowerCase().replace(/[^a-z0-9]/g, '');
        if (qn.length < 5) return [];
        const shard = await loadCodeShard(tpl, qn);
        if (!shard) return [];
        const maxDist = qn.length >= 9 ? 2 : 1;
        const minPrefix = Math.max(5, qn.length - 1);
        const scored = [];
        for (const key of Object.keys(shard)) {
            const d = editDistance(qn, key, maxDist);
            if (d <= maxDist) { scored.push({ key, score: d }); continue; }
            let n = 0;
            while (n < qn.length && n < key.length && qn[n] === key[n]) n++;
            if (n >= minPrefix) scored.push({ key, score: maxDist + 1 });
        }
        scored.sort((a, b) => a.score - b.score || Math.abs(a.key.length - qn.length) - Math.abs(b.key.length - qn.length) || (a.key < b.key ? -1 : 1));
        const picked = [];
        const seen = new Set();
        for (const { key } of scored) {
            for (const val of [].concat(shard[key])) {
                const [handle, variant] = val.split('|');
                if (seen.has(handle)) continue;
                seen.add(handle);
                picked.push({ handle, variant, exact: false });
            }
            if (picked.length >= limit) break;
        }
        return buildCodeItems(picked.slice(0, limit));
    };
    window.ceramedCodeIndex = { looksLikeCode, fetchItems: fetchCodeIndexItems, fetchSimilar: fetchSimilarCodeItems };
    const CN_ACTIVE = '!active';
    const CN_LOADING = '!loading';

    customElements.define('predictive-search', class extends Core {
        elements = {
            form: 'form',
            input: 'input[name="q"]',
            button: '[data-element="button"]',
            results: '[data-results]',
            wrapper: '[data-wrapper]',
            initial: '[data-initial]',
            suggestions: '*[data-suggestion]'
        }

        subscriptions = {
            'recentSearches:select': '_updateInputValue'
        }

        cache = {};

        render() {
            // TEMP: dirty lights off fix
            this.addEventListener('click', (e) => {
                this.$input.focus();
            });

            this._disableAutocomplete();
            this._handleInput();
            this._handleButtonClick();
            this._handleOuterClick();
            this._handleSuggestions();
            this._handleExactCodeSubmit();
            this._handleRecentSearches();
        }

        _disableAutocomplete() {
            this.$input.setAttribute('autocomplete', 'off');
        }

        _handleInput() {
            this.$input.addEventListener('focus', this._handleFocus.bind(this));
            this.$input.addEventListener('blur', this._handleBlur.bind(this));
            this.$input.addEventListener('input',  debounce(this._inputHandler.bind(this), 300).bind(this));
        }

        _handleButtonClick() {
            this.$button.addEventListener('click', this._handleButtonOnEmpty.bind(this))
        }

        _handleButtonOnEmpty(e) {
            if (this.$input.value) return;
            e.preventDefault();
            this.$input.focus();    
        }

        _handleFocus() {
            this._handleActiveState();
            this.lights = false;   
        }

        _handleBlur(e) {
            this.lights = true;
        }

        _handleActiveState() {
            if(this.isEmptyResults) {
                this.showInitial = false;
                this.active = true;
            } else {
                this.showInitial = true;
                this.active = !!this.minQueryChars || !!this.$initial;
            }
        }

        async _inputHandler() {
            if(!this.minQueryChars) {
                this.showResults = false;
                this.isEmptyResults = false;
                this._handleActiveState();
                return;
            }
            this.loading = true;
            await this._getQueryResults();
            this._renderQueryResults();
        }

        async _getQueryResults() {
            if (!this.cache[`${this.query}`]) {
                let $resultsDoc = null;
                // CERAMED: une saisie qui ressemble à un code (SKU, code-barres, SKU distributeur) est cherchée
                // d'abord par la recherche complète, que la recherche prédictive native ne trouve pas pour
                // les codes numériques ; sans résultat, on retombe sur le comportement standard.
                if (this.looksLikeCode) {
                    try {
                        // recherche complète + index des SKU distributeur (voir _fetchIndexItems), fusionnés
                        const [$codes, indexItems] = await Promise.all([
                            this._fetchCodesDoc().catch(() => null),
                            this._fetchIndexItems().catch(() => [])
                        ]);
                        $resultsDoc = this._mergeCodeResults($codes, indexItems);
                    } catch (e) { /* repli sur la recherche prédictive */ }
                }
                if (!$resultsDoc) $resultsDoc = await this._fetchResultsDoc();
                const empty = $resultsDoc.hasAttribute(ATTR_NO_RESULTS);
                this.cache[`${this.query}`] = {
                    empty,
                    content: $resultsDoc
                };
            }
        }

        async _fetchResultsDoc() {
            return $fetch(`${routes.predictive_search_url}?${this.searchParams}`, { select: '[data-template]'});
        }

        async _fetchCodesDoc() {
            const params = new URLSearchParams({ q: this.query, type: 'product', section_id: RQ_CODES_SECTION });
            const $doc = await $fetch(`${dynamicURLs.search}?${params.toString()}`, { select: '[data-template]'});
            // CERAMED: les correspondances exactes (data-exact) passent avant les résultats approchés
            const list = $doc && $doc.content && $doc.content.querySelector('[class*="-items"]');
            if (list) {
                Array.from(list.querySelectorAll(':scope > [data-exact]')).reverse().forEach(el => list.prepend(el));
            }
            return $doc;
        }

        // CERAMED: index des codes (SKU Ceramed ET SKU distributeur, normalisés : la recherche native ne trouve pas
        // un SKU saisi sans ses caractères spéciaux, et le métachamp sku.distributor n'est pas interrogeable).
        // La logique est dans fetchCodeIndexItems (en haut du fichier). 128 fichiers JSON (seau = empreinte des 3 premiers caractères du code normalisé), générés
        // chaque nuit et hébergés dans les Fichiers de Shopify ; adresse = réglage « predictive_search_code_index_url »
        // avec le marqueur {p} (numéro de seau). Entrées : code normalisé -> "handle|idVariant" (ou liste).
        // Réglage vide = fonction désactivée. Les produits retirés depuis la génération donnent une 404
        // à l'affichage de la ligne et sont simplement ignorés.
        async _fetchIndexItems() {
            return fetchCodeIndexItems(this.query, this.limit);
        }

        // CERAMED: fusionne les lignes de la recherche complète et de l'index (sans doublon, exactes d'abord,
        // limite de résultats) dans un <template data-template> de même forme que r_predictive-search.
        _mergeCodeResults($codes, indexItems) {
            const nodes = [];
            const frag = $codes && !$codes.hasAttribute(ATTR_NO_RESULTS) ? $codes.content : null;
            if (frag) nodes.push(...frag.querySelectorAll('[class*="-items"] > *'));
            nodes.push(...indexItems);
            const handleOf = (n) => {
                const a = n.querySelector('a.stretched-link');
                return a ? a.getAttribute('href').split('/products/')[1].split('?')[0] : null;
            };
            const seen = new Set();
            const unique = nodes.filter(n => {
                const h = handleOf(n);
                if (!h || seen.has(h)) return false;
                seen.add(h);
                return true;
            });
            if (!unique.length) return null;
            const ordered = [
                ...unique.filter(n => n.hasAttribute('data-exact')),
                ...unique.filter(n => !n.hasAttribute('data-exact'))
            ].slice(0, Number(this.limit));
            const tpl = document.createElement('template');
            tpl.setAttribute('data-template', '');
            tpl.innerHTML = '<div class="#predictive-search-results-types"><div class="#predictive-search-results-type">' +
                '<h3 class="#predictive-search-results-type-title">Products</h3>' +
                '<div class="#predictive-search-results-items"></div></div></div>';
            const list = tpl.content.querySelector('[class*="-items"]');
            ordered.forEach(n => list.appendChild(n.cloneNode(true)));
            return tpl;
        }

        // CERAMED: Entrée sur un code qui correspond exactement à UN seul produit : on va directement à la fiche.
        // On ATTEND les résultats de la saisie si la frappe est encore en cours de traitement (antirebond de 300 ms +
        // requêtes) : avant, un Entrée rapide arrivait avant les résultats et ouvrait la page de recherche.
        // Sans correspondance unique (ou en cas d'erreur), on envoie le formulaire normalement.
        _handleExactCodeSubmit() {
            this.$form.addEventListener('submit', async (e) => {
                if (!this.looksLikeCode) return;
                e.preventDefault();
                e.stopImmediatePropagation();
                let url = null;
                try {
                    await this._getQueryResults();
                    const cached = this.cache[`${this.query}`];
                    const frag = cached && cached.content && cached.content.content;
                    const exact = frag ? frag.querySelectorAll('[data-exact]') : [];
                    if (exact.length === 1) url = exact[0].getAttribute('data-exact-url');
                } catch (err) { /* envoi normal du formulaire */ }
                if (url) {
                    window.location.href = url;
                    return;
                }
                if (this.withRecent) this._publishQuery(this.query);
                this.$form.submit();
            });
        }

        _renderQueryResults() {
            $replaceContent(
                this.$results,
                this.cache[`${this.query}`].content
            )
            const isEmpty = this.cache[`${this.query}`].empty;
            this.isEmptyResults = !isEmpty;
            this._handleActiveState();
            this.loading = false;
            this.showResults = true;
        }

        _handleOuterClick() {
            document.addEventListener('click', (e) => {
                if(!this.contains(e.target) && this.active) {
                    this.active = false;
                }
            })
        }

        _handleSuggestions() {
            if(this.withSuggestions) {
                this.$suggestions.map($suggestion => {
                    $suggestion.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.$input.value = e.currentTarget.dataset.suggestion;
                        this.$input.focus();
                        
                        this.$input.dispatchEvent(new Event('input'));
                    })
                })
            }
        }

        _handleRecentSearches() {
            if (this.withRecent) {
                this.subscribe('recentSearches:select');
                this.addEventListener('click', (e) => {
                    if (e.target.href) this._publishQuery(this.query);
                }) 
                this.$form.addEventListener('submit', (e) => {
                    e.preventDefault();
                    const query = new FormData(this.$form).get('q');
                    this._publishQuery(query)
                    this.$form.submit();
                }, { once: true })
            };
        }

        _updateInputValue({value}) {
            this.$input.value = value;
            this.$input.focus();
        }

        _publishQuery(query) {
            this.publish('search:query', { query });
        }
        
        set loading(state) {
            this.classList.toggle(CN_LOADING, state);
        }

        set showInitial(state) {
            this.$initial && $toggleDisplay(this.$initial, state);
        }

        set showResults(state) {
            $toggleDisplay(this.$results, state);
        }

        set active(state) {
            this.$wrapper
                .classList
                .toggle(CN_ACTIVE, state);

            scrollLock(state);

            this._active = state;
        }

        get active() {
            return !!this._active;
        }

        get lights() {
            return this._lights;
        }

        set lights(state) {
            this.publish('lights-out', !state);
            this._lights = state;
        }

        get minQueryChars() {
            return this.query.length >= this.minChars;
        }

        // CERAMED: au moins 4 caractères, sans espace, lettres/chiffres/tirets/points/barres, avec au moins un chiffre
        get looksLikeCode() {
            return looksLikeCode(this.query);
        }

        get query() {
            return this.$input.value.trim();
        }

        get minChars() {
            return this.hasAttribute(ATTR_MIN_CHARS) 
                ? this.getAttribute(ATTR_MIN_CHARS) 
                : DEFAULT_MIN_CHARS;
        }

        get searchParams() {
            return new URLSearchParams({
                q: this.query,
                'resources[type]': this.sources,
                'resources[options][unavailable_products]': this.onlyStock,
                'resources[limit]': this.limit,
                'section_id': RQ_SECTION
            }).toString();
        }
        
        get sources() {
            return this.hasAttribute(ATTR_SOURCES) 
                ? this.getAttribute(ATTR_SOURCES).split(',').filter(Boolean).join(',')
                : '';
        }

        get onlyStock() {
            return this.hasAttribute(ATTR_ONLY_STOCK) 
                ? 'hide' 
                : 'last';
        }
        
        get limit() {
            return this.hasAttribute(ATTR_LIMIT) 
                ? this.getAttribute(ATTR_LIMIT)
                : DEFAULT_LIMIT;
        }

        get withSuggestions() {
            return this.hasAttribute(ATTR_WITH_SUGGESTIONS);
        }

        get withRecent() {
            return this.hasAttribute(ATTR_WITH_RECENT);
        }
    })
})();
